import { TrendingDown, TrendingUp, Ban, BadgeCheck } from "lucide-react";
import { Reveal } from "./Reveal";

const DIO_ITEMS = [
    "Dealer-installed 'protection packages'",
    "Distributor port add-ons & accessories",
    "Local markup extras — pinstripes, tint, guards",
];

const FIO_ITEMS = [
    "Factory Installed Options only",
    "Options that hold residual value",
    "Clean build sheet, stronger resale",
];

const BENEFITS = [
    {
        icon: TrendingDown,
        title: "Lower purchase price",
        body: "Consortium and fleet-volume pricing means your vehicle lands below Total Suggested Retail Price — including our fee. Members never fund a dealer's accessory shelf.",
    },
    {
        icon: Ban,
        title: "No low-residual add-ons",
        body: "DIO extras lose value the moment they are bolted on. We eliminate them from every member allocation.",
    },
    {
        icon: TrendingUp,
        title: "Higher resale value as a percentage of purchase price",
        body: "Factory-optioned vehicles appraise better and sell faster. Your RAV4 stays an asset, not a liability.",
    },
];

const OptionList = ({ title, tag, items, positive, testId }) => (
    <div
        data-testid={testId}
        className={`border p-8 ${
            positive ? "border-[#2563EB]/40 bg-[#0c0c0c]" : "border-white/10 bg-[#0a0a0a]"
        }`}
    >
        <p
            className={`text-xs font-bold uppercase tracking-[0.3em] ${
                positive ? "text-[#2563EB]" : "text-zinc-500"
            }`}
        >
            {tag}
        </p>
        <h3 className="mt-3 font-display text-xl font-bold uppercase tracking-tight sm:text-2xl">
            {title}
        </h3>
        <ul className="mt-6 space-y-4">
            {items.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm sm:text-base">
                    {positive ? (
                        <BadgeCheck
                            className="mt-0.5 h-4 w-4 shrink-0 text-[#2563EB]"
                            aria-hidden="true"
                        />
                    ) : (
                        <Ban className="mt-0.5 h-4 w-4 shrink-0 text-zinc-600" aria-hidden="true" />
                    )}
                    <span
                        className={
                            positive ? "text-zinc-200" : "text-zinc-500 line-through decoration-zinc-700"
                        }
                    >
                        {item}
                    </span>
                </li>
            ))}
        </ul>
    </div>
);

export const FioAdvantage = () => (
    <section
        id="fio-advantage"
        data-testid="fio-advantage-section"
        className="relative overflow-hidden py-28 sm:py-36"
    >
        <div className="mx-auto max-w-7xl px-6">
            <Reveal>
                <p className="mb-4 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.35em] text-[#2563EB]">
                    <span className="h-2 w-2 rotate-45 bg-[#2563EB]" aria-hidden="true" />
                    The FIO advantage
                </p>
                <h2 className="max-w-4xl font-display text-3xl font-extrabold uppercase leading-[1.02] tracking-tighter sm:text-5xl">
                    Pay only for Factory Installed Options.
                </h2>
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-zinc-400 md:text-lg">
                    Our members buy as one. Through bulk, fleet, and consortium purchasing power,
                    every RAV4 we allocate means you pay only for Factory Installed Options (FIO)
                    — never Dealer Installed Options (DIO), Local Installed Options (LIO), or
                    Distributor Installed Options (DIO) that inflate the price and evaporate at
                    resale or trade-in. Those are free if they're on the vehicle at all — our
                    negotiation team eliminates all costs associated with DIO or LIO.
                </p>
            </Reveal>

            <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2">
                <Reveal delay={0.1}>
                    <OptionList
                        tag="What you'll never pay for"
                        title="DIO — eliminated"
                        items={DIO_ITEMS}
                        positive={false}
                        testId="dio-eliminated-card"
                    />
                </Reveal>
                <Reveal delay={0.2}>
                    <OptionList
                        tag="What your RAV4 ships with"
                        title="FIO — guaranteed"
                        items={FIO_ITEMS}
                        positive
                        testId="fio-guaranteed-card"
                    />
                </Reveal>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-px bg-white/10 md:grid-cols-3">
                {BENEFITS.map((b, i) => (
                    <Reveal key={b.title} delay={i * 0.1} className="bg-[#050505]">
                        <div
                            data-testid={`fio-benefit-${i}`}
                            className="group h-full p-8 transition-colors duration-500 hover:bg-[#0c0c0c]"
                        >
                            <b.icon className="h-6 w-6 text-[#2563EB]" aria-hidden="true" />
                            <h3 className="mt-6 font-display text-lg font-bold uppercase tracking-tight sm:text-xl">
                                {b.title}
                            </h3>
                            <p className="mt-3 text-sm leading-relaxed text-zinc-400">{b.body}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);
